import { HeaderContainer } from "@/components/layout/HeaderContainer";
import { useModals } from "@/features/modals/hooks";
import styled from "@emotion/styled";
import {
  type Opportunity,
  OpportunityStatusEnum,
  updateProject,
} from "@repo/api";
import {
  Icon,
  LightMode,
  Text,
  Variables,
  checkmarkIcon,
  laptopPencilIcon,
  starIcon,
  warningTriangleIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import { FlexDiv, useToast } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

const ProgressContainter = styled(FlexDiv)`
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  flex-direction: column;
  width: 100%;
  padding: 0 ${Variables.space75};
  margin: -${Variables.space75} 0 0 0;
`;

interface ProgressLineProps {
  done?: boolean;
}
const ProgressLine = styled.hr<ProgressLineProps>`
  width: 100%;
  height: ${Variables.thicknessThin};
  background: ${({ done }) =>
    done ? LightMode.colourStaticIkeaBrandBlue : LightMode.colourNeutral5};
`;

const getProgress = (status?: Opportunity["status"], locked?: boolean) => {
  if (status === OpportunityStatusEnum.Unassigned) {
    return 0;
  }
  if (status === OpportunityStatusEnum.Assigned) {
    return 1;
  }
  if (status === OpportunityStatusEnum.InProgress) {
    return 2;
  }
  if (
    status === OpportunityStatusEnum.ClosedWon ||
    status === OpportunityStatusEnum.ClosedLost ||
    locked
  ) {
    return 3;
  }
  return 4;
};

interface ProjectProgressProps {
  opportunity?: Opportunity;
  editable?: boolean;
}

export const ProjectProgress = ({
  opportunity,
  editable,
}: ProjectProgressProps) => {
  const progress = getProgress(opportunity?.status, opportunity?.lockStatus);
  const { updateToast } = useToast();
  const { pushModal } = useModals();

  const onUpdateStatusHandler = (
    newStatus: Opportunity["status"],
    reason: string | undefined,
    description: string | undefined
  ) => {
    if (opportunity !== undefined) {
      updateProject(
        {
          id: opportunity?.id,
          status: newStatus,
          statusChangeDetails: reason ? { reason, description } : undefined,
        },
        updateToast
      );
    }
  };

  const interactiveOpportunity =
    editable && opportunity && !opportunity.lockStatus
      ? opportunity
      : undefined;
  const isInteractive = interactiveOpportunity !== undefined;

  const openStatusModal = interactiveOpportunity
    ? () => {
        pushModal({
          type: "PROJECT_PROGRESS",
          currentStatus: interactiveOpportunity.status,
          updateStatus: onUpdateStatusHandler,
        });
        trackAnalytics("open_project_status_change_modal");
      }
    : undefined;

  return (
    <HeaderContainer>
      <ProgressContainter
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        aria-label={isInteractive ? t("action.change_status") : undefined}
        className={
          isInteractive ? "saleslead__element--focus-visible" : undefined
        }
        onClick={openStatusModal}
        onKeyDown={
          isInteractive
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openStatusModal?.();
                }
              }
            : undefined
        }
      >
        <FlexDiv justifyContent="space-between" alignItems="center">
          <div>
            <Icon
              paths={starIcon}
              color={
                progress >= 1
                  ? LightMode.colourTextAndIcon1
                  : LightMode.colourTextAndIcon4
              }
            />
          </div>

          <ProgressLine done={progress > 1} />
          <div>
            <Icon
              paths={laptopPencilIcon}
              color={
                progress >= 2
                  ? LightMode.colourTextAndIcon1
                  : LightMode.colourTextAndIcon4
              }
            />
          </div>

          <ProgressLine done={progress > 2} />
          <div>
            {opportunity?.lockStatus ? (
              <Icon
                paths={warningTriangleIcon}
                color={LightMode.colourSemanticNegative}
              />
            ) : (
              <Icon
                paths={checkmarkIcon}
                color={
                  progress >= 3
                    ? LightMode.colourTextAndIcon1
                    : LightMode.colourTextAndIcon4
                }
              />
            )}
          </div>
        </FlexDiv>

        <FlexDiv justifyContent="space-between" grow margin="-10px">
          <Text
            color={
              progress >= 1
                ? LightMode.colourTextAndIcon1
                : LightMode.colourTextAndIcon3
            }
            bold={progress === 1}
          >
            <T translationKey="label.assigned" />
          </Text>
          <Text
            color={
              progress >= 2
                ? LightMode.colourTextAndIcon1
                : LightMode.colourTextAndIcon3
            }
            bold={progress === 2}
          >
            <T translationKey="status.inprogress" />
          </Text>
          {opportunity?.lockStatus ? (
            <Text
              color={LightMode.colourSemanticNegative}
              bold={opportunity?.lockStatus}
            >
              <T translationKey="label.locked" />
            </Text>
          ) : (
            <Text
              color={
                progress >= 3
                  ? LightMode.colourTextAndIcon1
                  : LightMode.colourTextAndIcon3
              }
              bold={progress >= 3}
            >
              {opportunity?.status === OpportunityStatusEnum.Invalid ? (
                <T translationKey="status.invalid" />
              ) : (
                <T translationKey="label.closed" />
              )}
            </Text>
          )}
        </FlexDiv>
      </ProgressContainter>
    </HeaderContainer>
  );
};
